import { useState } from "react";
import { NavLink } from "@/components/NavLink";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigation } from "@/contexts/NavigationContext";
import { 
  LayoutDashboard, 
  Inbox, 
  TrendingUp, 
  CheckSquare, 
  CalendarDays,
  FileText, 
  DollarSign, 
  Settings, 
  User,
  Plus,
  Crown,
  Sparkles,
  Archive,
  LogOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUserPlan } from "@/hooks/useUserPlan";
import { PlanComparisonModal } from "@/components/PlanComparisonModal";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";

interface SidebarProps {
  onQuickCapture: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

// Preload component code on hover for instant navigation
const preloadRoutes: Record<string, () => Promise<any>> = {
  '/dashboard': () => import("@/pages/Dashboard"),
  '/inbox': () => import("@/pages/SuperQueue"),
  '/sales': () => import("@/pages/Sales"),
  '/tasks': () => import("@/pages/TaskFlow"),
  '/calendar': () => import("@/pages/Calendar"),
  '/projects-clients': () => import("@/pages/ProjectsClients"),
  '/invoices': () => import("@/pages/Invoices"),
  '/finance': () => import("@/pages/Finance"),
  '/archive': () => import("@/pages/Archive"),
  '/settings': () => import("@/pages/Settings"),
};

const menuItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard", queryKey: ["dashboard-metrics"] },
  { icon: Inbox, label: "Unified Inbox", path: "/inbox", queryKey: ["queue-data"] },
  { icon: TrendingUp, label: "Sales", path: "/sales", queryKey: ["sales-leads"] },
  { icon: CheckSquare, label: "Task Flow", path: "/tasks", queryKey: ["tasks"] },
  { icon: CalendarDays, label: "Action Calendar", path: "/calendar", queryKey: ["calendar-events"] },
  { icon: User, label: "Projects & Clients", path: "/projects-clients", queryKey: ["clients"] },
  { icon: FileText, label: "Invoices", path: "/invoices", queryKey: ["invoices"] },
  { icon: DollarSign, label: "Finance", path: "/finance", queryKey: ["finance-transactions"] },
  { icon: Archive, label: "Archive", path: "/archive", queryKey: null },
  { icon: Settings, label: "Settings", path: "/settings", queryKey: null },
];

export const Sidebar = ({ onQuickCapture, isMobileOpen, onMobileClose }: SidebarProps) => {
  const { currentPlan } = useUserPlan();
  const [showPlanModal, setShowPlanModal] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { startNavigation } = useNavigation();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    queryClient.clear();
    window.location.href = '/auth';
  };

  // Prefetch data AND component code on hover for instant navigation
  const handlePrefetch = (path: string, queryKey: string[] | null) => {
    // Preload component code (if lazy loaded)
    preloadRoutes[path]?.();

    if (!queryKey || !user) return;

    const key = [...queryKey, user.id];

    // Check if data already exists in cache
    const cachedData = queryClient.getQueryData(key);
    if (cachedData) return; // Already cached

    // Prefetch based on route
    if (queryKey[0] === "dashboard-metrics") {
      queryClient.prefetchQuery({
        queryKey: key,
        staleTime: 5 * 60 * 1000,
      });
    } else if (queryKey[0] === "sales-leads") {
      queryClient.prefetchQuery({
        queryKey: key,
        queryFn: async () => {
          const { data } = await supabase
            .from("sales_leads")
            .select("*")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false });
          return data || [];
        },
      });
    } else if (queryKey[0] === "tasks") {
      queryClient.prefetchQuery({
        queryKey: [...key, false],
        queryFn: async () => {
          const { data } = await supabase
            .from("tasks")
            .select("*, projects(*, clients(*))")
            .eq("user_id", user.id)
            .neq('status', 'done')
            .order("created_at", { ascending: false });
          return data || [];
        },
      });
    } else if (queryKey[0] === "invoices") {
      queryClient.prefetchQuery({
        queryKey: [...key, undefined],
        queryFn: async () => {
          const { data } = await supabase
            .from("invoices")
            .select("*, clients(*)")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false });
          return data || [];
        },
      });
    }
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex h-16 items-center justify-center border-b border-sidebar-border px-6">
        <h1 className="text-xl font-bold">CatalystFlow</h1>
      </div>

      {/* Quick Capture Button */}
      <div className="p-4">
        <Button
          onClick={() => {
            onQuickCapture();
            if (isMobile) onMobileClose();
          }}
          className="w-full bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
        >
          <Plus className="mr-2 h-4 w-4" />
          Quick Capture
        </Button>
      </div>

      {/* Navigation - with component + data prefetching on hover */}
      <nav className="flex-1 space-y-1 px-3 py-2">
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onMouseEnter={() => handlePrefetch(item.path, item.queryKey)}
            onTouchStart={() => handlePrefetch(item.path, item.queryKey)}
            onClick={() => {
              startNavigation(item.label);
              if (isMobile) onMobileClose();
            }}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-sidebar-foreground/90 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
            activeClassName="bg-sidebar-accent text-sidebar-accent-foreground"
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Sign Out Button at bottom */}
      <div className="mt-auto p-4 border-t border-sidebar-border">
        <Button
          variant="ghost"
          onClick={handleSignOut}
          className="w-full justify-start gap-3 text-sidebar-foreground/90 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
        >
          <LogOut className="h-5 w-5" />
          Sign Out
        </Button>
      </div>
    </>
  );

  // Mobile: Render as Sheet drawer
  if (isMobile) {
    return (
      <>
        <Sheet open={isMobileOpen} onOpenChange={onMobileClose}>
          <SheetContent side="left" className="w-64 p-0 bg-sidebar text-sidebar-foreground">
            <div className="flex h-full flex-col">
              {sidebarContent}
            </div>
          </SheetContent>
        </Sheet>

        {/* Plan Comparison Modal */}
        <PlanComparisonModal 
          open={showPlanModal} 
          onOpenChange={setShowPlanModal}
          currentPlanType={currentPlan?.planType}
        />
      </>
    );
  }

  // Desktop: Render as static sidebar
  return (
    <>
      <aside className="flex w-64 flex-col bg-sidebar text-sidebar-foreground shadow-lg">
        {sidebarContent}
      </aside>

      {/* Plan Comparison Modal */}
      <PlanComparisonModal 
        open={showPlanModal} 
        onOpenChange={setShowPlanModal}
        currentPlanType={currentPlan?.planType}
      />
    </>
  );
};
